import { environmentConfig } from '../services/universalApi'
import {
  ensurePersistentAuthToken,
  getAuthToken,
  setAuthToken,
} from './authStorage'
import { isStandaloneDisplay } from './displayMode'

const REFRESH_PATH = '/auth/refresh'
/** Refresh when less than this many ms remain on the JWT. */
const REFRESH_WHEN_REMAINING_MS = 10 * 60 * 1000
/** Minimum gap between refresh attempts. */
const MIN_REFRESH_INTERVAL_MS = 30 * 1000

let inflight: Promise<boolean> | null = null
let lastAttemptAt = 0

function decodeJwtExpMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || '')) as { exp?: number }
    if (typeof payload.exp !== 'number') return null
    return payload.exp * 1000
  } catch {
    return null
  }
}

function tokenNeedsRefresh(token: string, force: boolean): boolean {
  const expMs = decodeJwtExpMs(token)
  if (expMs == null) return false
  const remaining = expMs - Date.now()
  if (remaining <= 0) return false // already expired — cannot silent-refresh
  if (force) return true
  return remaining < REFRESH_WHEN_REMAINING_MS
}

function preferPersistentStorage(): boolean {
  if (typeof window === 'undefined') return true
  if (isStandaloneDisplay()) return true
  return Boolean(localStorage.getItem('auth_token')?.trim())
}

/**
 * Sliding-session refresh while the access token is still valid.
 * Returns true when a new token was stored. Never logs the user out.
 */
export async function silentTokenRefresh(options?: {
  force?: boolean
}): Promise<boolean> {
  if (typeof window === 'undefined') return false
  ensurePersistentAuthToken()
  const token = getAuthToken()
  if (!token) return false
  if (!tokenNeedsRefresh(token, Boolean(options?.force))) return false

  const now = Date.now()
  if (!options?.force && now - lastAttemptAt < MIN_REFRESH_INTERVAL_MS) {
    return false
  }

  if (inflight) return inflight

  lastAttemptAt = now
  inflight = (async () => {
    try {
      const url = `${environmentConfig.apiBaseUrl}${REFRESH_PATH}`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      if (!res.ok) return false
      const data = (await res.json()) as { access_token?: string }
      const next = data.access_token?.trim()
      if (!next) return false
      setAuthToken(next, preferPersistentStorage())
      return true
    } catch {
      return false
    } finally {
      inflight = null
    }
  })()

  return inflight
}

/** Wire resume/focus silent refresh (PWA-friendly). Call once from app boot. */
export function installSilentTokenRefreshListeners(): () => void {
  if (typeof window === 'undefined') return () => undefined

  const onResume = () => {
    ensurePersistentAuthToken()
    void silentTokenRefresh()
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onResume()
  })
  window.addEventListener('pageshow', onResume)
  window.addEventListener('focus', onResume)

  // Initial promote + optional refresh
  onResume()

  return () => {
    document.removeEventListener('visibilitychange', onResume)
    window.removeEventListener('pageshow', onResume)
    window.removeEventListener('focus', onResume)
  }
}
